import React, { useMemo, useState } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Row, 
  Col, 
  Checkbox,
  Typography,
  Modal,
  message,
  Select
} from 'antd';
import { 
  SearchOutlined,
  EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminWithdrawalApi } from '@/services/api';

const { Title } = Typography;

interface WithdrawalData {
  key: string;
  no: number;
  academyCode: string;
  academyName: string;
  // 계약상태: 무료사용, 결제대기, 결제중, 결제실패, 해지
  contractStatus: 'free_usage' | 'payment_waiting' | 'payment_active' | 'payment_failed' | 'cancelled';
  withdrawalDate: string;
  // 탈퇴사유: 서비스 불만족, 비용 문제, 기타
  withdrawalReason: 'service_dissatisfaction' | 'cost_issue' | 'other';
  withdrawalReasonDetail: string;
  // 처리여부: 처리완료(날짜), 처리대기
  processingStatus: 'completed' | 'pending';
  processingDate?: string;
}

const WithdrawalManagement: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<WithdrawalData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [withdrawalConfirmModalVisible, setWithdrawalConfirmModalVisible] = useState(false); // 추가

  const fetchList = async (p = page, s = pageSize) => {
    try {
      const res: any = await adminWithdrawalApi.list({ academyCode: searchText || undefined, status: statusFilter, page: p - 1, size: s });
      const pageData = res?.data || res;
      const content = pageData?.content || [];
      const totalElements = pageData?.totalElements ?? content.length;
      const baseIndex = (p - 1) * s;
      const mapped: WithdrawalData[] = content.map((it: any, idx: number) => ({
        key: String(it.id),
        no: totalElements > 0 ? totalElements - (baseIndex + idx) : content.length - idx,
        academyCode: it.accountType === 'ACADEMY' ? (it.academyCode || '-') : '-',
        academyName: it.academyName || '-',
        contractStatus: 'payment_active',
        withdrawalDate: (it.requestedAt || '').toString().replace('T',' ').slice(0, 10),
        withdrawalReason: 'other',
        withdrawalReasonDetail: it.reason || '',
        processingStatus: it.status === 'APPROVED' ? 'completed' : 'pending',
        processingDate: it.processedAt ? String(it.processedAt).replace('T',' ').slice(0,10) : undefined,
      }));
      setRows(mapped);
      setTotal(pageData?.totalElements ?? mapped.length);
      setPage(pageData?.number != null ? (pageData.number + 1) : p);
      setPageSize(pageData?.size ?? s);
    } catch (e) {
      setRows([]);
      setTotal(0);
    }
  };

  React.useEffect(() => { fetchList(1, pageSize); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleSearch = () => { fetchList(1, pageSize); };

  const handleReset = () => { setSearchText(''); setStatusFilter(undefined); fetchList(1, pageSize); };

  const handleWithdrawalProcess = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('처리할 항목을 선택해주세요.');
      return;
    }
    setWithdrawalConfirmModalVisible(true); // 모달 표시
  };

  const handleWithdrawalConfirm = async () => {
    try {
      await adminWithdrawalApi.approve(selectedRowKeys.map(k => Number(k)));
      message.success(`${selectedRowKeys.length}개 항목이 처리되었습니다.`);
      setWithdrawalConfirmModalVisible(false);
      setSelectedRowKeys([]);
      fetchList(page, pageSize);
    } catch (e: any) {
      message.error(e?.response?.data?.message || '처리에 실패했습니다.');
    }
  };

  const handleWithdrawalCancel = () => {
    setWithdrawalConfirmModalVisible(false);
  };

  const handleReasonView = (reason: string) => {
    setSelectedReason(reason);
    setReasonModalVisible(true);
  };

  const allChecked = useMemo(() => rows.length > 0 && selectedRowKeys.length === rows.length, [rows, selectedRowKeys]);
  const indeterminate = useMemo(() => selectedRowKeys.length > 0 && selectedRowKeys.length < rows.length, [rows, selectedRowKeys]);

  const withdrawalColumns: ColumnsType<WithdrawalData> = [
    {
      title: <Checkbox
        checked={allChecked}
        indeterminate={indeterminate}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys(rows.map(item => item.key));
          } else {
            setSelectedRowKeys([]);
          }
        }}
      />,
      dataIndex: 'key',
      key: 'checkbox',
      width: 60,
      render: (key: string) => (
        <Checkbox
          checked={selectedRowKeys.includes(key)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, key]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(k => k !== key));
            }
          }}
        />
      )
    },
    { 
      title: '순번', 
      dataIndex: 'no', 
      key: 'no', 
      width: 80, 
      align: 'center' 
    },
    { 
      title: '학원코드', 
      dataIndex: 'academyCode', 
      key: 'academyCode', 
      width: 120 
    },
    { 
      title: '학원명', 
      dataIndex: 'academyName', 
      key: 'academyName', 
      width: 150 
    },
    { 
      title: '계약상태', 
      dataIndex: 'contractStatus', 
      key: 'contractStatus', 
      width: 120,
      render: (status: string) => {
        const statusConfig: Record<string, { text: string; color: string }> = {
          free_usage: { text: '무료사용', color: '#1890ff' },
          payment_waiting: { text: '결제대기', color: '#fa8c16' },
          payment_active: { text: '결제중', color: '#52c41a' },
          payment_failed: { text: '결제실패', color: '#f5222d' },
          cancelled: { text: '해지', color: '#8c8c8c' }
        };
        const config = statusConfig[status] || { text: status, color: '#000000' };
        return (
          <span style={{ 
            color: config.color, 
            fontWeight: 'bold' 
          }}>
            {config.text}
          </span>
        );
      }
    },
    { 
      title: '탈퇴신청일', 
      dataIndex: 'withdrawalDate', 
      key: 'withdrawalDate', 
      width: 120 
    },
    { 
      title: '탈퇴사유', 
      dataIndex: 'withdrawalReason', 
      key: 'withdrawalReason', 
      width: 150,
      render: (reason: string, record: WithdrawalData) => {
        return (
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleReasonView(record.withdrawalReasonDetail)}
          >
            상세내용
          </Button>
        );
      }
    },
    { 
      title: '처리여부', 
      dataIndex: 'processingStatus', 
      key: 'processingStatus', 
      width: 150,
      render: (status: string, record: WithdrawalData) => {
        if (status === 'completed') {
          return (
            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
              처리완료({record.processingDate})
            </span>
          );
        } else {
          return (
            <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>
              처리대기
            </span>
          );
        }
      }
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          탈퇴 신청관리
        </Title>

        {/* 검색 영역 */}
        <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Input
              placeholder="학원명, 학원코드로 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                검색
              </Button>
              <Button onClick={handleReset}>
                초기화
              </Button>
            </Space>
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="처리여부"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              style={{ width: 130 }}
              options={[
                { value: 'PENDING', label: '처리대기' },
                { value: 'APPROVED', label: '처리완료' },
              ]}
            />
          </Col>
          <Col style={{ marginLeft: 'auto' }}>
            <Button 
              type="primary" 
              danger
              onClick={handleWithdrawalProcess}
              disabled={selectedRowKeys.length === 0}
            >
              탈퇴 처리
            </Button>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={withdrawalColumns}
          dataSource={rows}
          pagination={{
            total,
            current: page,
            pageSize,
            onChange: (p, ps) => fetchList(p, ps as number),
            showTotal: (t, range) => {
              if (!t) return `0-0 / 총 0개`;
              const start = Math.max(t - range[1] + 1, 1);
              const end = Math.max(t - range[0] + 1, 1);
              return `${start}-${end} / 총 ${t}개`;
            },
          }}
          scroll={{ x: 1000 }}
        />

        {/* 탈퇴사유 모달 */}
        <Modal
          title="탈퇴사유 상세"
          open={reasonModalVisible}
          onCancel={() => setReasonModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setReasonModalVisible(false)}>
              닫기
            </Button>
          ]}
        >
          <p>{selectedReason}</p>
        </Modal>

        {/* 탈퇴 처리 확인 모달 */}
        <Modal
          title="탈퇴 처리 확인"
          open={withdrawalConfirmModalVisible}
          onCancel={handleWithdrawalCancel}
          footer={[
            <Button key="cancel" onClick={handleWithdrawalCancel}>
              취소
            </Button>,
            <Button 
              key="confirm" 
              type="primary" 
              danger 
              onClick={handleWithdrawalConfirm}
            >
              탈퇴 처리
            </Button>
          ]}
        >
          <div>
            <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>
              탈퇴처리 하시겠습니까?
            </p>
            <div style={{ 
              backgroundColor: '#fff2e8', 
              border: '1px solid #ffbb96', 
              borderRadius: '6px', 
              padding: '16px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: 0, color: '#d46b08', fontWeight: 'bold' }}>
                기타 주의사항
              </p>
              <p style={{ margin: '8px 0 0 0', color: '#d46b08' }}>
                탈퇴 로직 확정 후 텍스트 보완 예정
              </p>
            </div>
            <p style={{ margin: 0 }}>
              선택된 <strong>{selectedRowKeys.length}개</strong> 항목이 탈퇴 처리됩니다.
            </p>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default WithdrawalManagement; 